import React, { useMemo, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import useEntityTypes, { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';
import useGlobalConfig from 'helpers/useGlobalConfig';
import makeGetFieldsFetch from 'fetches/makeGetFieldsFetch';
import makeGetUserFieldsFetch from 'fetches/makeGetUserFieldsFetch';
import { useData } from 'resift';
import _get from 'lodash/get';
import _sortBy from 'lodash/sortBy';

import AddSectionDialog from './AddSectionDialog';

function Container({ currentLayout, typeKey, ...props }) {
  const globalConfig = useGlobalConfig();
  const entityTypes = useEntityTypes();
  const entityType = _get(entityTypes, typeKey);

  const makeGetFields = makeGetFieldsFetch();
  const entityTypeFields = useData(makeGetFields);

  const makeGetUserFields = makeGetUserFieldsFetch();
  const userFields = useData(makeGetUserFields);

  const fieldIdsInLayout = useMemo(() => {
    return getFieldIdReference(currentLayout);
  }, [currentLayout]);

  const ALLOWED_TYPES = ['collection', 'textLong', 'object'];

  const filterChildAttributes = useCallback(() => {
    const fieldKeys = Object.keys(entityType._fields);
    const filteredFieldItemKeys = fieldKeys.filter(key =>
      _get(entityType, `_fields.${key}.item.type`, false),
    );
    const filteredFieldKeys = fieldKeys.filter(key => entityType._fields[key].fields);
    const childItemKeys = filteredFieldItemKeys.filter(
      key => entityType._fields[key].item.type.charAt(0) === '@',
    );
    const extractedChildItemKeys = childItemKeys.map(itemKey => {
      const itemLength = entityType._fields[itemKey].item.type.length;
      return entityType._fields[itemKey].item.type.substring(8, itemLength);
    });
    let omittableChildItemFields = [];
    extractedChildItemKeys.forEach(item => {
      omittableChildItemFields.push(item);
    });
    filteredFieldKeys.forEach(item => {
      entityType._fields[item].fields.forEach(field => {
        omittableChildItemFields.push(field);
      });
    });

    const filteredFieldList = fieldKeys.filter(field => {
      const targetField = entityType._fields[field];
      return (
        !omittableChildItemFields.includes(field) &&
        targetField.baseField !== 'page' &&
        (ALLOWED_TYPES.includes(targetField.type) ||
          ['pronouns', 'inclusivePronouns'].includes(targetField.objectKey) ||
          _get(targetField, 'input.components', []).includes('pageCollection')) &&
        !fieldIdsInLayout[targetField.id]
      );
    });

    return filteredFieldList;
  }, [fieldIdsInLayout]);

  const availableAttributes = useMemo(() => {
    if (typeKey === PERSON_ENTITY_TYPE_KEY && userFields) {
      const parentAttributes = filterChildAttributes();
      const parentFields = _sortBy(
        parentAttributes.map(attribute =>
          _get(userFields, 'results', []).find(field => field.objectKey === attribute),
        ),
        field => {
          return field.displayName.toLowerCase();
        },
      );
      return parentFields;
    } else if (entityTypeFields) {
      const parentAttributes = filterChildAttributes();
      const parentFields = _sortBy(
        parentAttributes.map(attribute =>
          _get(entityTypeFields, 'results', []).find(field => field.objectKey === attribute),
        ),
        field => {
          return field.displayName.toLowerCase();
        },
      );
      return parentFields;
    }
    return [];
  }, [entityTypeFields, userFields, fieldIdsInLayout]);

  return (
    <AddSectionDialog
      globalConfig={globalConfig}
      availableAttributes={availableAttributes}
      {...props}
    />
  );
}

Container.propTypes = {
  currentLayout: PropTypes.array,
};

export default memo(Container);

function getFieldIdReference(layout) {
  const fieldIdReference = {};
  layout.forEach(item => recursiveFieldGetter(item, fieldIdReference));
  return fieldIdReference;
}

function recursiveFieldGetter(item, fieldIds) {
  if (item.type === 'category' && _get(item, 'value.children')) {
    item.value.children.forEach(childItem => {
      recursiveFieldGetter(childItem, fieldIds);
    });
  } else if (item.type === 'field') {
    fieldIds[item.value.id] = true;
  }
}
